<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { breakpointsBootstrapV5, useBreakpoints, useEventListener } from "@vueuse/core";
import { TuBasePopup, TuButton, ButtonSeverity } from "@tinyurldev/tu-ui-vue";
import { FocusTrap } from "focus-trap-vue";

import { useTranslation } from "@Resources/js/composables";
import XOverlay from "@Modules/shared/store/XOverlay";
import type { ApiToken, ApiTokenPayload } from "@Modules/teams/Data/Models/ApiToken";
import CreateEditTokenForm from "@Modules/teams/components/CreateEditTokenForm.vue";

//
// Setup
const overlay = XOverlay();
const { __ } = useTranslation();

//
// Props
interface Props {
  token: ApiToken;
  submit: (payload: ApiTokenPayload) => Promise<ApiToken | null>;
  errors?: Record<string, string>;
  loading?: boolean;
  save?: () => void;
  clearErrors?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => ({}),
  loading: false,
  save: () => {},
  clearErrors: () => {},
});

//
// Data
const formRef = ref<InstanceType<typeof CreateEditTokenForm> | null>(null);

//
// Computed
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");
const canSubmit = computed(() => formRef.value?.isValid === true && !props.loading);

//
// Methods
function close(): void {
  if (props.loading) {
    return;
  }

  props.clearErrors();
  overlay.hide();
}

function onUpdated(): void {
  props.save();
  close();
}

useEventListener(window, "keydown", (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    close();
  }
});

onMounted(() => {
  document.body.style.overflow = "hidden";
});

onBeforeUnmount(() => {
  document.body.style.overflow = "";
});

//
// Exposed Test Vars
defineExpose({ close, onUpdated });
</script>

<template>
  <div
    class="edit-api-token-modal-wrapper _token_modal"
    :class="{ 'edit-api-token-modal-wrapper--mobile': isMobileView }"
    @click.self="close"
  >
    <FocusTrap :escape-deactivates="false">
      <TuBasePopup
        id="edit-api-token-modal"
        tabindex="-1"
        class="edit-api-token-modal"
        :class="{ 'edit-api-token-modal--mobile': isMobileView }"
        :is-closeable="!isMobileView"
        @close="close"
      >
        <template #title>
          <span class="_title edit-api-token-modal__title">
            {{ __("organization_api_settings.edit_token_modal.title") }}
          </span>
        </template>

        <template #content>
          <CreateEditTokenForm
            ref="formRef"
            class="edit-api-token-modal__form"
            :editable-token="token"
            :submit="submit"
            :errors="errors"
            :loading="loading"
            :is-submit-visible="false"
            @update="onUpdated"
          />
        </template>

        <template #footer>
          <div class="edit-api-token-modal__footer">
            <TuButton
              v-if="!isMobileView"
              class="_cancel"
              data-test-id="edit-api-token-cancel"
              :label="__('organization_api_settings.edit_token_modal.cancel_button')"
              is-outlined
              :severity="ButtonSeverity.CANCEL"
              :is-disabled="loading"
              @click="close"
            />
            <TuButton
              class="_submit"
              data-test-id="edit-api-token-submit"
              :label="__('organization_api_settings.edit_token_modal.submit_button')"
              :is-block="isMobileView"
              :is-loading="loading"
              :is-disabled="!canSubmit"
              @click="formRef?.doSubmit()"
            />
          </div>
        </template>
      </TuBasePopup>
    </FocusTrap>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/position.scss" as position;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.edit-api-token-modal-wrapper {
  position: fixed;
  inset: 0;
  z-index: position.$highest-z-index;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: spacing.$xxs;

  background-color: color.$main-overlay;

  &--mobile {
    padding: 0;
  }
}

.edit-api-token-modal {
  max-width: 600px;

  /**
   * The popup body already supplies the surface, so the shared form drops its card chrome here.
   * Scoped styles reach it because a child component's root node carries the parent's scope id.
   */
  &__form {
    padding: 0;

    background-color: transparent;
    border: 0;
    border-radius: 0;
  }

  &__title {
    font-size: typography.$h5-font-size;
    font-weight: typography.$font-weight-bold;
    line-height: typography.$h5-line-height;
  }

  &__footer {
    display: flex;
    gap: spacing.$xxs;
    align-items: end;
    justify-content: flex-end;
  }

  &--mobile {
    margin-top: auto;
  }
}
</style>
